import { useEffect, useState } from 'react';
import { usePushSubscription, type NotificationOptInStatus } from '../hooks/usePushSubscription.js';
import type { Auth } from '../lib/auth.js';

const STORAGE_KEY = 'scope:push:banner-dismissed';

interface NotificationOptInBannerProps {
  auth: Auth;
  /** Visual variant; the portal one is lighter to fit the lighter shell. */
  variant?: 'dark' | 'light';
}

/**
 * Dismissible banner that prompts the signed-in user to enable push
 * notifications. Required on iOS — Apple only fires the permission
 * prompt when triggered from a user gesture (a real click), so we
 * can't ask on mount the way Chrome on Android allows.
 *
 * Visibility rules: only renders when the browser supports push AND
 * permission is currently 'default' (i.e. unanswered) AND the user
 * hasn't dismissed it. Hidden once granted, denied, unsupported, or
 * dismissed. Stays mounted in the shell so it doesn't flash during
 * navigation.
 */
export function NotificationOptInBanner({ auth, variant = 'dark' }: NotificationOptInBannerProps) {
  const { status, isWorking, subscribe } = usePushSubscription(auth);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Watch for status changes (e.g. permission granted in another tab) and
  // close the banner immediately.
  useEffect(() => {
    if (status === 'granted' || status === 'denied' || status === 'unsupported') {
      // No need to remember dismissal — the banner won't show anyway.
    }
  }, [status]);

  const visible =
    !dismissed && auth.kind !== 'anonymous' && status === ('default' as NotificationOptInStatus);
  if (!visible) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // silently fail — banner will reappear next visit, fine
    }
    setDismissed(true);
  };

  const handleEnable = async () => {
    const next = await subscribe();
    if (next === 'granted' || next === 'denied') {
      // Either way it won't reappear — we can drop the dismissal flag.
      // (Granted → predicate hides it; denied → predicate also hides it.)
    }
  };

  // Style per shell.
  const wrapperClass =
    variant === 'light'
      ? 'mb-4 flex flex-col gap-3 rounded-xl border border-scope-border-soft bg-status-blue-bg/40 p-4 sm:flex-row sm:items-center sm:justify-between'
      : 'mb-4 flex flex-col gap-3 rounded-xl border border-status-blue/30 bg-status-blue/5 p-4 sm:flex-row sm:items-center sm:justify-between';

  return (
    <div className={wrapperClass}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-status-blue/10 text-status-blue">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-scope-navy">
            Get notified the moment things happen
          </p>
          <p className="mt-0.5 text-xs text-scope-text-muted">
            Approvals, document requests and new reports — straight to your phone, even when this
            tab isn't open.
          </p>
          <p className="mt-1 text-2xs text-scope-text-faint">
            On iPhone, add Scope to your home screen first, then enable from inside the installed
            app.
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button
          onClick={handleDismiss}
          className="rounded-md px-3 py-1.5 text-xs text-scope-text-muted hover:bg-white hover:text-scope-navy"
          disabled={isWorking}
        >
          Not now
        </button>
        <button
          onClick={() => void handleEnable()}
          disabled={isWorking}
          className="rounded-md bg-scope-navy px-3 py-1.5 text-xs font-medium text-white transition hover:bg-scope-navy-soft disabled:opacity-60"
        >
          {isWorking ? 'Enabling…' : 'Enable notifications'}
        </button>
      </div>
    </div>
  );
}
